/**
 * 수행평가 루브릭 예시/양식 - SEO 롱테일 페이지
 * 
 * 타겟 키워드: 수행평가 루브릭, 루브릭 예시, 루브릭 양식, 서술형 채점 기준표
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Footer from '../../components/layout/Footer';
import '../../styles/design-system/index.css';

const RubricGuidePage = () => {
  const navigate = useNavigate();
  const [selectedSubject, setSelectedSubject] = useState('국어');

  const subjects = ['국어', '영어', '수학', '과학', '사회', '예체능'];

  const rubricExamples = {
    '국어': {
      title: '논술형 평가 루브릭',
      criteria: [
        { name: '내용', desc: '주제에 대한 이해와 근거 제시' },
        { name: '구성', desc: '서론-본론-결론의 논리적 구성' },
        { name: '표현', desc: '어휘 사용과 문장 표현력' },
        { name: '분량', desc: '적정 분량 충족 여부' }
      ],
      levels: ['상 (A)', '중 (B)', '하 (C)'],
      sample: [
        ['주제를 정확히 파악하고 다양한 근거를 제시함', '주제를 파악하고 1-2개의 근거를 제시함', '주제 파악이 불명확하거나 근거가 부족함'],
        ['서론-본론-결론이 명확하고 논리적 흐름이 자연스러움', '전체 구성은 갖추었으나 일부 논리적 비약이 있음', '구성이 불완전하거나 논리적 흐름이 부족함'],
        ['풍부한 어휘와 다양한 문장 구조를 활용함', '적절한 어휘와 문장을 사용함', '어휘가 단조롭고 문장이 어색함'],
        ['제시된 분량을 충족함', '분량의 80% 이상 작성함', '분량의 80% 미만 작성함']
      ]
    },
    '영어': {
      title: '영어 말하기 평가 루브릭',
      criteria: [
        { name: 'Fluency', desc: '유창성과 자연스러움' },
        { name: 'Accuracy', desc: '문법 및 어휘 정확성' },
        { name: 'Pronunciation', desc: '발음과 억양' },
        { name: 'Content', desc: '내용의 적절성' }
      ],
      levels: ['상 (A)', '중 (B)', '하 (C)'],
      sample: [
        ['막힘없이 자연스럽게 말함', '가끔 머뭇거리나 대체로 자연스러움', '자주 머뭇거리고 끊어서 말함'],
        ['문법과 어휘 오류가 거의 없음', '간혹 오류가 있으나 의미 전달에 지장 없음', '오류가 많아 의미 파악이 어려움'],
        ['원어민에 가까운 발음과 자연스러운 억양', '발음이 대체로 명확함', '발음이 불명확하여 이해가 어려움'],
        ['주제에 맞는 풍부한 내용을 제시함', '주제에 맞는 내용을 제시함', '주제와 관련성이 부족함']
      ]
    },
    '수학': {
      title: '수학 문제해결 평가 루브릭',
      criteria: [
        { name: '이해', desc: '문제 상황의 이해' },
        { name: '전략', desc: '해결 전략 수립' },
        { name: '실행', desc: '계산 및 풀이 과정' },
        { name: '검증', desc: '결과 확인 및 반성' }
      ],
      levels: ['상 (A)', '중 (B)', '하 (C)'],
      sample: [
        ['문제를 정확히 이해하고 조건을 파악함', '문제를 이해하나 일부 조건을 놓침', '문제 이해가 불충분함'],
        ['적절하고 효율적인 해결 전략을 수립함', '해결 전략을 수립하나 비효율적임', '해결 전략이 적절하지 않음'],
        ['풀이 과정이 논리적이고 계산이 정확함', '풀이 과정에 사소한 오류가 있음', '풀이 과정에 중대한 오류가 있음'],
        ['결과를 검증하고 오류를 수정함', '결과를 확인함', '결과 검증을 하지 않음']
      ]
    },
    '과학': {
      title: '과학 탐구 보고서 루브릭',
      criteria: [
        { name: '탐구 설계', desc: '가설 설정 및 변인 통제' },
        { name: '탐구 수행', desc: '실험 과정의 정확성' },
        { name: '결과 분석', desc: '데이터 해석 및 결론 도출' },
        { name: '보고서 작성', desc: '형식과 표현의 적절성' }
      ],
      levels: ['상 (A)', '중 (B)', '하 (C)'],
      sample: [
        ['명확한 가설을 설정하고 변인을 적절히 통제함', '가설을 설정하고 변인 통제를 시도함', '가설이 불명확하거나 변인 통제가 미흡함'],
        ['실험 과정을 정확하게 수행함', '실험 과정에 사소한 오류가 있음', '실험 과정에 중대한 오류가 있음'],
        ['데이터를 정확히 분석하고 타당한 결론을 도출함', '데이터를 분석하고 결론을 도출함', '데이터 분석이나 결론 도출이 미흡함'],
        ['보고서 형식을 잘 갖추고 명확하게 표현함', '보고서 형식을 갖추었으나 일부 미흡함', '보고서 형식이 불완전함']
      ]
    },
    '사회': {
      title: '사회 프로젝트 발표 루브릭',
      criteria: [
        { name: '내용', desc: '주제 탐구의 깊이' },
        { name: '자료', desc: '자료 수집 및 활용' },
        { name: '발표', desc: '발표 기술' },
        { name: '협력', desc: '모둠 협력' }
      ],
      levels: ['상 (A)', '중 (B)', '하 (C)'],
      sample: [
        ['주제를 심층적으로 탐구하고 다양한 관점을 제시함', '주제를 탐구하고 적절한 내용을 제시함', '주제 탐구가 피상적임'],
        ['다양하고 신뢰할 수 있는 자료를 적절히 활용함', '관련 자료를 수집하여 활용함', '자료 수집이나 활용이 미흡함'],
        ['발표 내용을 명확하게 전달하고 청중과 소통함', '발표 내용을 전달함', '발표 내용 전달이 불명확함'],
        ['모둠원 모두가 적극적으로 참여하고 역할을 수행함', '대부분의 모둠원이 역할을 수행함', '일부 모둠원만 참여함']
      ]
    },
    '예체능': {
      title: '실기 평가 루브릭 (체육/미술/음악)',
      criteria: [
        { name: '기능', desc: '기본 기능 숙달도' },
        { name: '표현', desc: '창의적 표현력' },
        { name: '태도', desc: '수업 참여 태도' },
        { name: '발전', desc: '향상도' }
      ],
      levels: ['상 (A)', '중 (B)', '하 (C)'],
      sample: [
        ['기본 기능을 정확하게 수행함', '기본 기능을 대체로 수행함', '기본 기능 수행에 어려움이 있음'],
        ['자신만의 창의적인 표현이 돋보임', '표현이 적절함', '표현이 단조롭거나 부족함'],
        ['적극적으로 참여하고 노력하는 모습을 보임', '성실하게 참여함', '참여가 소극적임'],
        ['이전 대비 뚜렷한 향상을 보임', '약간의 향상을 보임', '향상이 거의 없음']
      ]
    }
  };

  const currentRubric = rubricExamples[selectedSubject];

  return (
    <>
      <Helmet>
        <title>수행평가 루브릭 예시 & 양식 | 과목별 채점 기준표 모음 (2026)</title>
        <meta name="description" content="수행평가 루브릭 예시 및 양식 모음. 국어 논술, 영어 말하기, 수학 문제해결, 과학 탐구, 사회 프로젝트 과목별 채점 기준표. AI로 맞춤형 루브릭 자동 생성." />
        <meta name="keywords" content="수행평가 루브릭, 루브릭 예시, 루브릭 양식, 서술형 채점 기준표, 채점 기준표 양식, 평가 기준표, 루브릭 만들기, 수행평가 평가표" />
        <link rel="canonical" href="https://whatquiz.co.kr/guide/rubric" />
        
        <meta name="robots" content="index, follow" />
        
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://whatquiz.co.kr/guide/rubric" />
        <meta property="og:title" content="수행평가 루브릭 예시 & 양식 | 과목별 채점 기준표" />
        <meta property="og:description" content="국어, 영어, 수학, 과학, 사회 과목별 수행평가 루브릭 예시." />
        <meta property="og:image" content="https://whatquiz.co.kr/og-image-whatquiz-2025.png?v=20260210" />
        <meta property="og:locale" content="ko_KR" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "수행평가 루브릭 예시 및 양식 모음",
            "description": "과목별 서술형 채점 기준표(루브릭) 예시와 작성 가이드",
            "author": { "@type": "Organization", "name": "왓퀴즈" },
            "publisher": { "@type": "Organization", "name": "왓퀴즈" },
            "datePublished": "2026-02-05",
            "dateModified": "2026-02-05"
          })}
        </script>
      </Helmet>

      <div style={{ 
        fontFamily: 'var(--whatquiz-font-family)',
        backgroundColor: '#fff',
        minHeight: '100vh'
      }}>
        {/* 히어로 */}
        <section style={{
          padding: '80px 20px 60px',
          textAlign: 'center',
          background: 'linear-gradient(180deg, #dbeafe 0%, #fff 100%)'
        }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <span style={{
              display: 'inline-block',
              padding: '6px 16px',
              backgroundColor: '#bfdbfe',
              color: '#1e40af',
              borderRadius: '20px',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '16px'
            }}>
              과목별 채점 기준표
            </span>
            
            <h1 style={{
              fontSize: 'clamp(26px, 5vw, 40px)',
              fontWeight: '800',
              color: '#111',
              lineHeight: '1.3',
              marginBottom: '20px'
            }}>
              수행평가 루브릭<br />
              <span style={{ color: '#2563eb' }}>예시 & 양식</span>
            </h1>
            
            <p style={{
              fontSize: '17px',
              color: '#64748b',
              lineHeight: '1.7',
              marginBottom: '32px'
            }}>
              국어, 영어, 수학, 과학, 사회, 예체능<br />
              <strong>과목별 채점 기준표 모음</strong>
            </p>
          </div>
        </section>

        {/* 과목 선택 */}
        <section style={{
          padding: '40px 20px',
          maxWidth: '1000px',
          margin: '0 auto'
        }}>
          <h2 style={{
            fontSize: '20px',
            fontWeight: '700',
            color: '#111',
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            과목 선택
          </h2>

          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px',
            justifyContent: 'center',
            marginBottom: '40px'
          }}>
            {subjects.map((subj) => (
              <button
                key={subj}
                onClick={() => setSelectedSubject(subj)}
                style={{
                  padding: '10px 24px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: selectedSubject === subj ? '#fff' : '#374151',
                  backgroundColor: selectedSubject === subj ? '#2563eb' : '#f1f5f9',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {subj}
              </button>
            ))}
          </div>

          {/* 루브릭 표 */}
          <div style={{
            padding: '24px',
            backgroundColor: '#f8fafc',
            borderRadius: '16px'
          }}>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#111',
              marginBottom: '24px',
              textAlign: 'center'
            }}>
              {currentRubric.title}
            </h3>

            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '13px'
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#e2e8f0' }}>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #cbd5e1', fontWeight: '600', minWidth: '120px' }}>평가 기준</th>
                    {currentRubric.levels.map((level, i) => (
                      <th key={i} style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #cbd5e1', fontWeight: '600', minWidth: '180px' }}>{level}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {currentRubric.criteria.map((criterion, idx) => (
                    <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                      <td style={{ padding: '12px', borderBottom: '1px solid #e5e7eb' }}>
                        <strong>{criterion.name}</strong>
                        <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>{criterion.desc}</div>
                      </td>
                      {currentRubric.sample[idx].map((desc, i) => (
                        <td key={i} style={{ 
                          padding: '12px', 
                          borderBottom: '1px solid #e5e7eb',
                          color: '#374151',
                          lineHeight: '1.5'
                        }}>
                          {desc}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section style={{
          padding: '80px 20px',
          textAlign: 'center',
          background: 'linear-gradient(180deg, #fff 0%, #eff6ff 100%)'
        }}>
          <div style={{ maxWidth: '600px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: '28px',
              fontWeight: '700',
              color: '#111',
              marginBottom: '16px'
            }}>
              서술형 채점,<br />
              <span style={{ color: '#3182f6' }}>AI가 2분 만에</span>
            </h2>
            
            <p style={{
              fontSize: '16px',
              color: '#64748b',
              lineHeight: '1.7',
              marginBottom: '32px'
            }}>
              루브릭 기준에 따라<br />
              <strong>AI가 자동으로 채점 & 피드백</strong>
            </p>

            <button
              onClick={() => navigate('/scoring')}
              style={{
                padding: '16px 40px',
                fontSize: '16px',
                fontWeight: '600',
                color: '#fff',
                backgroundColor: '#3182f6',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer'
              }}
            >
              AI 서술형 채점하기
            </button>
          </div>
        </section>

        {/* 관련 가이드 */}
        <section style={{
          padding: '40px 20px',
          maxWidth: '800px',
          margin: '0 auto'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#111',
            marginBottom: '16px'
          }}>
            관련 가이드
          </h3>
          
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <Link to="/guide/setak-example" style={{
              padding: '12px 20px',
              backgroundColor: '#f1f5f9',
              borderRadius: '8px',
              color: '#334155',
              textDecoration: 'none',
              fontSize: '14px'
            }}>
              세특 예시 모음
            </Link>
            <Link to="/guide/saenggibu-2026" style={{
              padding: '12px 20px',
              backgroundColor: '#f1f5f9',
              borderRadius: '8px',
              color: '#334155',
              textDecoration: 'none',
              fontSize: '14px'
            }}>
              2026 생기부 기재요령
            </Link>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default RubricGuidePage;
